// GAVION deal amount visibility (supabase/schemas/72_gavion_deal_amount_visibility.sql): a Setter / Apporteur only
// receives no amount (deals_visible: amount null, amount_hidden true). It reads « — » with « Valeur masquée » as its
// tooltip, and a total that leaves such amounts out says so.
import { required, useRecordContext, useTranslate } from "ra-core";
import { NumberInput } from "@/components/admin/number-input";
import { dealArr, dealMrr } from "../domain/deals/dealValue";
import { formatCad } from "../domain/format";
import {
  isAmountHidden,
  isMonthlyAmount,
  type AmountRow,
} from "./dealAmountModel";

export const HiddenAmount = ({ className }: { className?: string }) => {
  const translate = useTranslate();
  const label = translate("gavion.deals.amount.hidden");
  return (
    <span
      className={className}
      title={label}
      aria-label={label}
      data-amount-hidden="true"
    >
      —
    </span>
  );
};

/**
 * The deal's amount in CAD, or « — » (tooltip « Valeur masquée ») when it is hidden from the viewer. GAVION deal package
 * (76_): a deal with a « Forfait proposé » reads « 149 $ /mois » (its value is the MRR).
 */
export const DealAmount = ({
  deal,
  format = formatCad,
  className,
}: {
  deal: AmountRow;
  format?: (amount: number | null | undefined) => string;
  className?: string;
}) =>
  isAmountHidden(deal) ? (
    <HiddenAmount className={className} />
  ) : isMonthlyAmount(deal) ? (
    <MonthlyAmount amount={format(deal.amount)} className={className} />
  ) : (
    <span className={className}>{format(deal.amount)}</span>
  );

/**
 * The board card's value (owner ruling): « 249 $ /mois · 2 988 $ /an » — package: its MRR; manual amount: annual, so
 * MRR = amount / 12. A hidden amount (72_) reads « — » as everywhere.
 */
export const DealMrrArr = ({
  deal,
  className,
}: {
  deal: AmountRow & { proposed_mrr?: number | string | null };
  className?: string;
}) => {
  const translate = useTranslate();
  if (isAmountHidden(deal)) return <HiddenAmount className={className} />;
  return (
    <span className={className} data-amount-mrr-arr="true">
      {translate("gavion.deals.board.mrr_arr", {
        mrr: formatCad(dealMrr(deal)),
        arr: formatCad(dealArr(deal)),
      })}
    </span>
  );
};

const MonthlyAmount = ({
  amount,
  className,
}: {
  amount: string;
  className?: string;
}) => {
  const translate = useTranslate();
  return (
    <span className={className} data-amount-monthly="true">
      {amount} {translate("gavion.deals.proposal.per_month")}
    </span>
  );
};

/** « Certaines valeurs sont masquées », when a total left some amounts out. */
export const HiddenAmountsNote = ({
  count,
  className = "text-xs text-muted-foreground",
}: {
  count: number | null | undefined;
  className?: string;
}) => {
  const translate = useTranslate();
  if (!count || count <= 0) return null;
  return (
    <p className={className} data-amounts-hidden-note="true">
      {translate("gavion.deals.amount.hidden_note")}
    </p>
  );
};

/**
 * The deal form's amount: a NumberInput, or — when the amount is hidden from the viewer — its label and « — », with no
 * input at all (nothing to validate, nothing to send; the database keeps the stored amount anyway).
 */
export const DealAmountInput = () => {
  const record = useRecordContext<AmountRow>();
  const translate = useTranslate();
  if (isAmountHidden(record))
    return (
      <div className="flex flex-col gap-1" data-amount-input-hidden="true">
        <span className="text-sm font-medium">
          {translate("resources.deals.fields.amount")}
        </span>
        <HiddenAmount className="text-sm" />
      </div>
    );
  return (
    <NumberInput
      source="amount"
      defaultValue={0}
      helperText={false}
      validate={required()}
    />
  );
};

/**
 * A report sum (72_): « — » when every amount behind it is hidden from the viewer, else the sum of the amounts he may
 * see, marked « * » (tooltip « Certaines valeurs sont masquées ») when some were left out.
 */
export const ReportAmount = ({
  amount,
  hidden,
  rows,
  format = formatCad,
}: {
  amount: number | null | undefined;
  hidden: number | null | undefined;
  /** How many deals the sum is over (all hidden: « — »). */
  rows: number;
  format?: (amount: number | null | undefined) => string;
}) => {
  const translate = useTranslate();
  const h = hidden ?? 0;
  if (h > 0 && h >= rows) return <HiddenAmount />;
  if (h <= 0) return <>{format(amount)}</>;
  const note = translate("gavion.deals.amount.hidden_note");
  return (
    <span title={note}>
      {format(amount)}
      <span aria-label={note} data-amount-partial="true">
        {" *"}
      </span>
    </span>
  );
};
